import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, LogOut, RefreshCw, PoundSterling, CalendarCheck, FileText, Zap, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retried && !original.url.includes("/auth/")) {
      original._retried = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch {}
    }
    return Promise.reject(error);
  }
);

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
};

const inputCls = "w-full bg-[#0E0E0E] border border-neutral-800 focus:border-torch outline-none rounded-sm px-4 py-3 text-sm text-white placeholder:text-neutral-600 transition-colors";

const StatusPill = ({ status }) => {
  const styles = {
    confirmed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    contacted: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    done: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    cancelled: "border-red-500/40 bg-red-500/10 text-red-400",
    pending_payment: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    new: "border-torch/40 bg-torch/10 text-torch",
  };
  return (
    <span className={`font-mono text-[10px] uppercase tracking-wider border px-2.5 py-1 rounded-sm whitespace-nowrap ${styles[status] || "border-neutral-700 text-neutral-400"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ stats: null, bookings: [], quotes: [] });

  const loadDashboard = useCallback(async () => {
    const [stats, bookings, quotes] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/bookings"),
      api.get("/admin/quotes"),
    ]);
    setData({ stats: stats.data, bookings: bookings.data, quotes: quotes.data });
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); return loadDashboard(); })
      .catch(() => setUser(false));
  }, [loadDashboard]);

  const updateStatus = async (kind, ref, status) => {
    try {
      await api.post(`/admin/${kind}/${ref}/status`, { status });
      toast.success(`Marked as ${status}`);
      await loadDashboard();
    } catch {
      toast.error("Could not update — try again");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data: u } = await api.post("/auth/login", form);
      setUser(u);
      await loadDashboard();
      toast.success("Welcome back");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
    setData({ stats: null, bookings: [], quotes: [] });
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-torch animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-obsidian flex items-center justify-center px-4">
        <div className="grain-overlay" />
        <div className="max-w-sm w-full bg-surface border border-neutral-800 rounded-sm p-8">
          <div className="flex justify-center mb-6">
            <img src="/images/logo-mark.png" alt="Teign Gas" className="h-10 w-auto mix-blend-screen" />
          </div>
          <h1 className="font-display font-extrabold text-xl text-white uppercase text-center tracking-tight">Owner dashboard</h1>
          <p className="text-xs text-neutral-500 text-center mt-2 font-mono tracking-wider uppercase">Teign Gas staff only</p>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input data-testid="admin-email-input" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="admin-password-input" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full bg-torch hover:bg-torch-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide py-3.5 rounded-sm flex items-center justify-center gap-2 transition-all">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const { stats, bookings, quotes } = data;

  const statCards = [
    { icon: PoundSterling, label: "Paid revenue", value: stats ? `£${stats.revenue_gbp.toFixed(2)}` : "…" },
    { icon: CalendarCheck, label: "Paid bookings", value: stats?.bookings_paid ?? "…" },
    { icon: FileText, label: "New quotes", value: stats?.quotes_new ?? "…" },
    { icon: Zap, label: "Emergency callouts", value: stats?.emergencies ?? "…" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-obsidian text-neutral-100">
      <div className="grain-overlay" />
      <header className="border-b border-white/10 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <img src="/images/logo-mark.png" alt="Teign Gas" className="h-8 w-auto mix-blend-screen" />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block font-mono text-[10px] tracking-wider text-neutral-500 uppercase">{user.email}</span>
            <button onClick={loadDashboard} data-testid="admin-refresh-btn" className="border border-neutral-700 hover:border-torch text-neutral-300 hover:text-torch p-2.5 rounded-sm transition-colors" aria-label="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 border border-neutral-700 hover:border-red-500 text-neutral-300 hover:text-red-400 font-mono text-xs uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="bg-surface border border-neutral-800 rounded-sm p-5">
              <s.icon className="w-4 h-4 text-torch mb-3" />
              <p className="font-mono font-bold text-2xl text-white">{s.value}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <section>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-xl text-white mb-4">Bookings</h2>
          <div className="border border-neutral-800 rounded-sm overflow-x-auto" data-testid="admin-bookings-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-neutral-800 bg-surface">
                  {["Ref", "Customer", "Service", "Slot", "Amount", "Status", "Actions"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800/60">
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">No bookings yet</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.ref} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3.5 font-mono text-xs text-torch">{b.ref}</td>
                    <td className="px-4 py-3.5">
                      <p className="text-white font-medium">{b.name}</p>
                      <p className="text-xs text-neutral-500">{b.phone} · {b.postcode}</p>
                    </td>
                    <td className="px-4 py-3.5 text-neutral-300">{b.service_name}</td>
                    <td className="px-4 py-3.5 text-neutral-300 whitespace-nowrap">{b.date}<br /><span className="text-xs text-neutral-500">{b.slot}</span></td>
                    <td className="px-4 py-3.5 font-mono text-white">£{Number(b.amount).toFixed(2)}</td>
                    <td className="px-4 py-3.5"><StatusPill status={b.status} /></td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {b.status === "confirmed" && (
                        <div className="flex gap-2">
                          <button onClick={() => updateStatus("bookings", b.ref, "completed")} data-testid={`booking-complete-${b.ref}`} className="font-mono text-[10px] uppercase tracking-wider border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-2.5 py-1.5 rounded-sm transition-colors">Complete</button>
                          <button onClick={() => updateStatus("bookings", b.ref, "cancelled")} data-testid={`booking-cancel-${b.ref}`} className="font-mono text-[10px] uppercase tracking-wider border border-neutral-700 text-neutral-400 hover:border-red-500 hover:text-red-400 px-2.5 py-1.5 rounded-sm transition-colors">Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-xl text-white mb-4">Quote requests</h2>
          <div className="border border-neutral-800 rounded-sm overflow-x-auto" data-testid="admin-quotes-table">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-neutral-800 bg-surface">
                  {["Ref", "Customer", "Job", "Urgency", "Ballpark", "Message", "Actions"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] tracking-[0.2em] text-neutral-500 uppercase px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800/60">
                {quotes.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-500">No quote requests yet</td></tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.ref} className="hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3.5 font-mono text-xs text-torch">{q.ref}</td>
                    <td className="px-4 py-3.5">
                      <p className="text-white font-medium">{q.name}</p>
                      <p className="text-xs text-neutral-500">{q.phone}</p>
                    </td>
                    <td className="px-4 py-3.5 text-neutral-300 capitalize">{q.property_type} · {q.boiler_type}</td>
                    <td className="px-4 py-3.5"><StatusPill status={q.urgency} /></td>
                    <td className="px-4 py-3.5 font-mono text-white whitespace-nowrap">{q.estimate || "—"}</td>
                    <td className="px-4 py-3.5 text-neutral-400 max-w-[220px] truncate">{q.message || "—"}</td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      {(!q.status || q.status === "new") && (
                        <button onClick={() => updateStatus("quotes", q.ref, "contacted")} data-testid={`quote-contact-${q.ref}`} className="font-mono text-[10px] uppercase tracking-wider border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 px-2.5 py-1.5 rounded-sm transition-colors">Contacted</button>
                      )}
                      {q.status === "contacted" && (
                        <button onClick={() => updateStatus("quotes", q.ref, "done")} data-testid={`quote-done-${q.ref}`} className="font-mono text-[10px] uppercase tracking-wider border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 px-2.5 py-1.5 rounded-sm transition-colors">Done</button>
                      )}
                      {q.status === "done" && <StatusPill status="done" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
